import { createContext, useContext, useEffect, useState, useCallback, useMemo, type ReactNode } from 'react';
import { withSentry } from '../utils/sentryLazy';
import { supabase } from '../services/supabaseClient';

// ── FEAT-6: Trial-expiring email trigger ──────────────────────────
// Fix 2026-09-22 (tq82): esta función vivía acá y se llamaba con
// profiles.trial_ends_at/is_paid — un reloj separado y desincronizado del
// que ya usan el banner y el gate (subscriptions.trial_ends_at/status,
// migrados en tq81). Movida a src/services/TrialEmailService.ts y ahora se
// llama desde CompanyContext.tsx, justo donde se resuelve `subscription`
// — la MISMA fuente que isTrialExpired/daysRemaining, no una copia.

// ── Types ─────────────────────────────────────────────────────────
export type UserRole = 'contador' | 'emprendedor' | 'admin' | null;

interface UserRoleContextType {
  role: UserRole;
  /**
   * viewMode: immediate UI override for the Emprendedor/Contador toggle.
   * Reads from localStorage ('quipus_view_mode') first so the sidebar and
   * route guards react instantly — without waiting for the Supabase round-trip.
   * Falls back to `role` when not set.
   */
  viewMode: UserRole;
  setViewMode: (r: NonNullable<UserRole>) => void;
  loading: boolean;
  /**
   * company_id from the profiles table.
   * Only set for emprendedor accounts — used to restrict which company they see.
   * null for contador/admin (they see all companies).
   */
  profileCompanyId: string | null;
  saveRole: (role: UserRole) => Promise<{ error: unknown }>;
  /**
   * UTC Date when the 7-day free trial ends (profiles.trial_ends_at). null
   * if not yet set.
   * @deprecated (tq82, 2026-09-22) — no tiene consumidores externos reales
   * (confirmado por grep en todo el repo): nada fuera de este archivo
   * destructura `trialEndsAt`/`isPaid` de useUserRole(). El banner y el
   * gate de trial usan subscription.trial_ends_at/status en
   * CompanyContext.tsx desde tq81 — esa es la fuente de verdad real. Se
   * deja sin borrar a pedido explícito (ver commit de tq82) — evaluar en
   * un fix aparte si conviene remover este campo, su estado interno, y la
   * columna profiles.trial_ends_at en sí (que sigue teniendo UN escritor
   * real: el bloque de inicialización de "primer login" en fetchProfile()
   * más abajo — no evaluado en este fix).
   */
  trialEndsAt: Date | null;
  /**
   * true once the user has an active paid subscription (profiles.is_paid).
   * @deprecated (tq82, 2026-09-22) — mismo caso que trialEndsAt arriba.
   */
  isPaid: boolean;
  /** ISO timestamp of when the user consented to the privacy policy. null = not yet consented. */
  privacyConsentAt: string | null;
  /**
   * Waitlist gate: 'waitlist' = pending admin approval, 'beta' = approved,
   * null = legacy user (unrestricted access).
   */
  plan: string | null;
}

interface ProfileRow {
  role:                string | null;
  company_id:          string | null;
  trial_starts_at:     string | null;
  trial_ends_at:       string | null;
  is_paid:             boolean | null;
  privacy_consent_at:  string | null;
  plan:                string | null;
}

// ── Context ───────────────────────────────────────────────────────
const UserRoleContext = createContext<UserRoleContextType>({
  role: null,
  viewMode: null,
  setViewMode: () => {},
  loading: true,
  profileCompanyId: null,
  saveRole: async () => ({ error: null }),
  trialEndsAt: null,
  isPaid: false,
  privacyConsentAt: null,
  plan: null,
});

// ── Helpers ────────────────────────────────────────────────────────
function parseRole(meta: Record<string, unknown> | undefined): UserRole {
  const r = meta?.role;
  if (r === 'contador' || r === 'emprendedor' || r === 'admin') return r;
  return null;
}

// ── Provider — ONE subscription for the entire app ────────────────
export function UserRoleProvider({ children }: { children: ReactNode }) {
  const [role,             setRole]             = useState<UserRole>(null);
  const [viewModeState,    setViewModeState]    = useState<UserRole>(() => {
    const stored = localStorage.getItem('quipus_view_mode');
    if (stored === 'emprendedor' || stored === 'contador') return stored;
    return null;
  });
  const [loading,          setLoading]          = useState(true);
  const [profileCompanyId, setProfileCompanyId] = useState<string | null>(null);
  const [trialEndsAt,      setTrialEndsAt]      = useState<Date | null>(null);
  const [isPaid,           setIsPaid]           = useState(false);
  const [privacyConsentAt, setPrivacyConsentAt] = useState<string | null>(null);
  const [plan,             setPlan]             = useState<string | null>(null);
  // SEC-5: tracks active session user id. null when logged out.
  // The inactivity effect mounts listeners only while this is non-null.
  const [sessionUserId,    setSessionUserId]    = useState<string | null>(null);

  const setViewMode = useCallback((r: NonNullable<UserRole>) => {
    localStorage.setItem('quipus_view_mode', r);
    setViewModeState(r);
  }, []);

  // Fetch role + trial info from the profiles table.
  // Falls back to user_metadata.role if the profiles row doesn't exist yet.
  // On first login (trial_starts_at IS NULL), writes 7-day trial window to DB.
  const fetchProfile = useCallback(async (userId: string, metaRole: UserRole) => {
    if (!userId) return;

    const { data, error: fetchError } = await supabase
      .from('profiles')
      .select('role, company_id, trial_starts_at, trial_ends_at, is_paid, privacy_consent_at, plan')
      .eq('id', userId)
      .maybeSingle();

    // Fail-closed (fix #7, auditoría 2026-08-15): un error real del SELECT
    // (red, RLS transitorio, 5xx) es indistinguible de "sin fila" si solo
    // miramos `data` — pero handle_new_user() garantiza que la fila SIEMPRE
    // existe, así que data===null con error!=null solo puede significar que
    // no pudimos leerla, nunca que el usuario sea nuevo. Cortar acá evita
    // dos problemas: (1) dejar `plan` en null, que WaitlistGate no bloquea
    // (solo bloquea con 'waitlist' exacto); (2) que el bloque de "primer
    // login" de abajo trate a un usuario existente como nuevo y sobreescriba
    // trial_starts_at/trial_ends_at con un upsert.
    if (fetchError) {
      console.error('[UserRoleContext] profiles fetch failed — fail-closed:', fetchError.message);
      setPlan('waitlist');
      setLoading(false);
      return;
    }

    const row = data as ProfileRow | null;

    if (row) {
      const profileRole = parseRole(row as unknown as Record<string, unknown>);
      setRole(profileRole ?? metaRole);
      setProfileCompanyId(row.company_id ?? null);
      setIsPaid(row.is_paid ?? false);
      setTrialEndsAt(row.trial_ends_at ? new Date(row.trial_ends_at) : null);
      setPrivacyConsentAt(row.privacy_consent_at ?? null);
      setPlan(row.plan ?? null);
    } else {
      setRole(metaRole);
      setProfileCompanyId(null);
    }

    // First login: trial_starts_at not yet set → initialise 7-day trial.
    if (!row?.trial_starts_at) {
      const now      = new Date();
      const trialEnd = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
      console.log('[UserRoleContext] initialising trial — trial_starts_at was null, new trialEnd:', trialEnd.toISOString());
      const { error } = await supabase
        .from('profiles')
        .upsert(
          {
            id:               userId,
            trial_starts_at:  now.toISOString(),
            // Preserve existing trial_ends_at if already set in DB.
            // Prevents resetting a valid trial window when only trial_starts_at was null.
            trial_ends_at:    row?.trial_ends_at ?? trialEnd.toISOString(),
            is_paid:          false,
            // New signups start on waitlist — admin sets plan = 'beta' to approve.
            // Only set when the row doesn't already have a plan (avoids overwriting 'beta').
            ...(row?.plan == null ? { plan: 'waitlist' } : {}),
          },
          { onConflict: 'id' },
        );
      if (error) {
        console.error('[UserRoleContext] trial upsert failed:', error.message);
      } else {
        // Update local state only if DB didn't already have a trial_ends_at.
        // If it did, setTrialEndsAt was already called from the row read above.
        if (!row?.trial_ends_at) {
          setTrialEndsAt(trialEnd);
        }
        setIsPaid(false);
        if (row?.plan == null) setPlan('waitlist');
      }
    }

    // Fix tq82 (2026-09-22): el trigger de email de "trial por vencer" se
    // movió a CompanyContext.tsx (lee subscriptions, no profiles) — ver
    // TrialEmailService.ts. Ya no se dispara desde acá.

    setLoading(false);
  }, []);

  useEffect(() => {
    // SUBSCRIBE-ONCE-ON-MOUNT.
    //
    // Deps son `[]` (no `[fetchProfile]`) intencionalmente: queremos que el
    // listener de auth se monte UNA SOLA VEZ por instancia del provider. Aunque
    // `fetchProfile` es `useCallback([])` y debería tener identidad estable,
    // dejarlo en deps abre la puerta a re-suscripciones espurias (StrictMode
    // unmount-remount, hot reload, re-mount del padre). Cada re-suscripción
    // dispara INITIAL_SESSION sincrónicamente → vuelve a llamar fetchProfile →
    // re-loggea "[UserRoleContext] initialising trial". El usuario veía decenas
    // del mismo log + re-renders innecesarios de TrialBanner.
    //
    // fetchProfile está definido en el mismo provider con `useCallback([])` y
    // captura solo state setters de React (estables por contrato), por eso es
    // seguro referenciarlo desde un effect de mount-único sin incluirlo en deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const meta = session?.user?.user_metadata as Record<string, unknown> | undefined;
      const metaRole = parseRole(meta);
      if (session?.user?.id) {
        setSessionUserId(session.user.id);
        withSentry((S) => S.setUser({ id: session.user.id }));
        void fetchProfile(session.user.id, metaRole);
      } else {
        setSessionUserId(null);
        withSentry((S) => S.setUser(null));
        setRole(metaRole);
        setProfileCompanyId(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // FEAT-9-FASE-1: write to BOTH auth.user_metadata AND profiles.role.
  // Pre-fix bug: fetchProfile() reads profiles.role (canónico) but saveRole()
  // only wrote to user_metadata, so role changes appeared to "stick" in the
  // current session (state was set locally + metadata) but did NOT persist
  // across logins because the next fetchProfile() would re-read the stale
  // profiles.role from DB. Fix writes to both transactionally.
  const saveRole = useCallback(async (newRole: UserRole): Promise<{ error: unknown }> => {
    if (!sessionUserId) return { error: new Error('No active session') };

    // 1) auth.user_metadata — fast, used by route guards (App.tsx)
    const { error: metaErr } = await supabase.auth.updateUser({ data: { role: newRole } });
    if (metaErr) return { error: metaErr };

    // 2) profiles.role — canonical source read by fetchProfile()
    const { error: profileErr } = await supabase
      .from('profiles')
      .upsert({ id: sessionUserId, role: newRole }, { onConflict: 'id' });
    if (profileErr) {
      console.warn('[UserRoleContext] saveRole: profiles update failed — auth metadata is now ahead of DB', profileErr.message);
      return { error: profileErr };
    }

    setRole(newRole);
    return { error: null };
  }, [sessionUserId]);

  const viewMode = viewModeState ?? role;

  const contextValue = useMemo(() => ({
    role, viewMode, setViewMode, loading, profileCompanyId,
    saveRole, trialEndsAt, isPaid, privacyConsentAt, plan,
  }), [role, viewMode, setViewMode, loading, profileCompanyId, saveRole, trialEndsAt, isPaid, privacyConsentAt, plan]);

  return (
    <UserRoleContext.Provider value={contextValue}>
      {children}
    </UserRoleContext.Provider>
  );
}

// ── Hook — drop-in replacement for old useUserRole import ─────────
export function useUserRole() {
  return useContext(UserRoleContext);
}
